/**
 * Service for semantic search functionality
 */

/**
 * Generates an embedding for the provided search query
 * @param {string} query - The search query
 * @returns {Promise<Object>} - Object containing embedding or error
 */
export const generateEmbedding = async (query) => {
  try {
    console.log(`Generating embedding for query: "${query}"`);
    
    // Make the API call to our serverless function
    const response = await fetch('/.netlify/functions/generate-embedding', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    console.log('Response status:', response.status);
    
    // Parse the response as text first for debugging
    const responseText = await response.text();
    console.log('Response text:', responseText);
    
    // Try to parse the response as JSON
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (jsonError) {
      console.error('Error parsing JSON:', jsonError);
      throw new Error(`Failed to parse response as JSON: ${responseText}`);
    }
    
    if (!response.ok) {
      console.error('Error from API:', data);
      throw new Error(data.error || data.details || 'Failed to generate embedding');
    }

    if (!data.embedding) {
      console.error('No embedding in response:', data);
      throw new Error('No embedding found in response');
    }
    
    console.log(`Successfully generated embedding with ${data.embedding.length} dimensions`);
    return { embedding: data.embedding, error: null };
  } catch (error) {
    console.error('Embedding generation error:', error);
    return { embedding: null, error: error.message };
  }
};

/**
 * Calculates cosine similarity between two vectors
 * @param {Array<number>} vecA - First vector
 * @param {Array<number>} vecB - Second vector
 * @returns {number} - Similarity score between 0 and 1
 */
export const cosineSimilarity = (vecA, vecB) => {
  const dotProduct = vecA.reduce((sum, a, i) => sum + a * vecB[i], 0);
  const magnitudeA = Math.sqrt(vecA.reduce((sum, a) => sum + a * a, 0));
  const magnitudeB = Math.sqrt(vecB.reduce((sum, b) => sum + b * b, 0));
  return dotProduct / (magnitudeA * magnitudeB);
};

/**
 * Finds jobs similar to the provided embedding
 * @param {Array<number>} queryEmbedding - The query embedding vector
 * @param {Array<Object>} jobEmbeddings - Array of job embeddings objects
 * @param {number} limit - Maximum number of results to return
 * @returns {Object} - Object containing results array or error
 */
export const findSimilarJobs = (queryEmbedding, jobEmbeddings, limit = 50) => {
  try {
    if (!jobEmbeddings || !Array.isArray(jobEmbeddings) || !queryEmbedding) {
      throw new Error('Invalid inputs for similarity search');
    }
    
    // Calculate similarity scores for each job
    const results = jobEmbeddings
      .map(item => ({
        cis_fuss_id: item.cis_fuss_id, // Use cis_fuss_id as the primary identifier
        similarity: cosineSimilarity(queryEmbedding, item.embedding)
      }))
      .sort((a, b) => b.similarity - a.similarity) // Sort by similarity (highest first)
      .slice(0, limit); // Limit to specified number
    
    return { results, error: null };
  } catch (error) {
    console.error('Error finding similar jobs:', error);
    return { results: [], error: error.message };
  }
};